import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import styled, { keyframes, css } from 'styled-components';
import { useVoiceCall } from '../contexts/VoiceCallContext';
import { useLanguage } from '../contexts/LanguageContext';

const pulseAnim = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.35; transform: scale(0.75); }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Wrapper = styled.div`
  position: fixed;
  bottom: 148px;
  right: 20px;
  z-index: 10000;
  width: 320px;
  background: #2a2a2a;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.45);
  animation: ${slideUp} 0.2s ease;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  overflow: hidden;
  color: white;

  @media (max-width: 768px) {
    right: 16px;
    width: calc(100vw - 32px);
    max-width: 320px;
  }
`;

const HUDHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 16px 0;
  color: rgba(255, 255, 255, 0.6);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.3px;
  text-transform: uppercase;
`;

const Body = styled.div`
  padding: 10px 16px 16px;
`;

const StatusRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 28px;
  margin-bottom: 10px;
`;

const StatusDot = styled.span<{ variant: 'pulse' | 'solid' | 'ring' }>`
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
  ${({ variant }) => {
    if (variant === 'solid') return css`background: #f34452;`;
    if (variant === 'ring') return css`background: transparent; border: 2px solid #f34452;`;
    return css`background: #f34452; animation: ${pulseAnim} 1.2s ease-in-out infinite;`;
  }}
`;

const StatusText = styled.span`
  color: rgba(255, 255, 255, 0.85);
  font-size: 13px;
`;

const Divider = styled.div`
  height: 1px;
  background: rgba(255, 255, 255, 0.08);
  margin: 6px 0 10px;
`;

const TranscriptLabel = styled.div`
  color: rgba(255, 255, 255, 0.4);
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  margin-bottom: 6px;
`;

const TranscriptScroll = styled.div`
  max-height: 150px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 5px;
  margin-bottom: 12px;

  &::-webkit-scrollbar { width: 3px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: rgba(255, 255, 255, 0.15); border-radius: 2px; }
`;

const TranscriptLine = styled.div<{ isAgent: boolean }>`
  font-size: 12px;
  line-height: 1.5;
  color: ${({ isAgent }) => isAgent ? 'rgba(255,255,255,0.88)' : 'rgba(255,255,255,0.55)'};

  &::before {
    content: '${({ isAgent }) => isAgent ? 'AI' : 'You'}';
    color: ${({ isAgent }) => isAgent ? '#f34452' : 'rgba(255,255,255,0.35)'};
    font-weight: 700;
    font-size: 10px;
    margin-right: 5px;
    text-transform: uppercase;
    letter-spacing: 0.3px;
  }
`;

const ErrorBox = styled.div`
  font-size: 13px;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.8);
  background: rgba(243, 68, 82, 0.12);
  border-left: 3px solid #f34452;
  border-radius: 0 6px 6px 0;
  padding: 8px 10px;
  margin-bottom: 12px;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 8px;
`;

const EndButton = styled.button`
  flex: 1;
  height: 36px;
  background: #f34452;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;

  &:hover { background: #d63946; }
  &:focus { outline: none; box-shadow: 0 0 0 3px rgba(243, 68, 82, 0.35); }
`;

const GhostButton = styled.button`
  height: 36px;
  padding: 0 14px;
  background: transparent;
  color: rgba(255, 255, 255, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 8px;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;

  &:hover { border-color: rgba(255, 255, 255, 0.35); color: white; }
  &:focus { outline: none; box-shadow: 0 0 0 3px rgba(243, 68, 82, 0.3); }
`;

const PhoneIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

const VoiceCallHUD: React.FC = () => {
  const { status, error, transcript, isSpeaking, endCall, startCall, dismissHUD } = useVoiceCall();
  const { t } = useLanguage();
  const endButtonRef = useRef<HTMLButtonElement>(null);
  const transcriptEndRef = useRef<HTMLDivElement>(null);

  // Focus end button when connected
  useEffect(() => {
    if (status === 'connected') endButtonRef.current?.focus();
  }, [status]);

  // Scroll transcript to bottom on new messages
  useEffect(() => {
    transcriptEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [transcript.length]);

  if (status === 'idle') return null;

  const getStatusDot = () => {
    if (status === 'error' || status === 'ended') return null;
    if (status === 'connected' && isSpeaking) return <StatusDot variant="solid" />;
    if (status === 'connected') return <StatusDot variant="ring" />;
    return <StatusDot variant="pulse" />;
  };

  const getStatusText = () => {
    if (status === 'requesting_mic' || status === 'connecting') return t('voice.call.connecting');
    if (status === 'connected' && isSpeaking) return t('voice.call.ai_speaking');
    if (status === 'connected') return t('voice.call.listening');
    if (status === 'ended') return t('voice.call.ended');
    return null;
  };

  const getErrorText = () => {
    switch (error) {
      case 'mic_denied': return t('voice.call.error.mic_denied');
      case 'mic_unavailable': return t('voice.call.error.mic_unavailable');
      case 'session_failed': return t('voice.call.error.session_failed');
      case 'connection_lost': return t('voice.call.error.connection_lost');
      default: return t('voice.call.error.session_failed');
    }
  };

  const statusText = getStatusText();

  const hud = (
    <Wrapper role="dialog" aria-label={t('voice.call.aria.start')} aria-live="polite">
      <HUDHeader>
        <PhoneIcon />
        {t('voice.call.start')}
      </HUDHeader>
      <Body>
        {statusText && (
          <StatusRow>
            {getStatusDot()}
            <StatusText>{statusText}</StatusText>
          </StatusRow>
        )}

        {status === 'error' && <ErrorBox>⚠ {getErrorText()}</ErrorBox>}

        {transcript.length > 0 && status !== 'error' && (
          <>
            <Divider />
            <TranscriptLabel>{t('voice.call.transcript_label')}</TranscriptLabel>
            <TranscriptScroll>
              {transcript.map((entry, i) => (
                <TranscriptLine key={i} isAgent={entry.role === 'agent'}>
                  {entry.text}
                </TranscriptLine>
              ))}
              <div ref={transcriptEndRef} />
            </TranscriptScroll>
          </>
        )}

        <ButtonRow>
          {status === 'error' ? (
            <>
              <EndButton onClick={startCall}>{t('voice.call.retry')}</EndButton>
              <GhostButton onClick={dismissHUD}>{t('voice.call.close')}</GhostButton>
            </>
          ) : status === 'ended' ? (
            <GhostButton onClick={dismissHUD} style={{ flex: 1 }}>{t('voice.call.close')}</GhostButton>
          ) : (
            <EndButton ref={endButtonRef} onClick={endCall} aria-label={t('voice.call.aria.end')}>
              {t('voice.call.end')}
            </EndButton>
          )}
        </ButtonRow>
      </Body>
    </Wrapper>
  );

  return ReactDOM.createPortal(hud, document.body);
};

export default VoiceCallHUD;
